import { useEffect, useState, useCallback } from "react";
import { api, apiError } from "@/lib/api";
import PageHeader from "@/components/common/PageHeader";
import DataTable from "@/components/common/DataTable";
import Pagination from "@/components/common/Pagination";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { fmtDate } from "@/lib/format";
import { ScrollText } from "lucide-react";

const ACTIONS = ["", "login", "logout", "failed_login", "password_reset", "user_creation", "user_deactivation",
  "lead_creation", "duplicate_override", "lead_reassignment", "lead_merge", "stage_change",
  "payment_verification", "commission_adjustment", "commission_approval", "data_export",
  "gps_tracking_start", "gps_tracking_stop", "settings_update"];

export default function AuditLogs() {
  const [action, setAction] = useState("all");
  const [page, setPage] = useState(1);
  const [data, setData] = useState({ items: [], total: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setLoading(true); setError("");
    try {
      const params = { page, page_size: 20 };
      if (action !== "all") params.action = action;
      const { data } = await api.get("/audit", { params });
      setData(data);
    } catch (e) { setError(apiError(e)); } finally { setLoading(false); }
  }, [action, page]);
  useEffect(() => { load(); }, [load]);

  const columns = [
    { key: "created_at", label: "Time", render: (r) => fmtDate(r.created_at, true) },
    { key: "action", label: "Action", render: (r) => <span className="font-mono text-xs bg-slate-100 rounded px-2 py-1">{r.action}</span> },
    { key: "actor_name", label: "Actor", render: (r) => r.actor_name || "System" },
    { key: "entity_type", label: "Entity", mobileHide: true, render: (r) => r.entity_type || "—" },
    { key: "ip", label: "IP", mobileHide: true, render: (r) => r.ip || "—" },
  ];

  return (
    <div className="space-y-6">
      <PageHeader title="Audit Logs" subtitle="Immutable record of security and data events." testid="audit-header"
        actions={
          <Select value={action} onValueChange={(v) => { setPage(1); setAction(v); }}>
            <SelectTrigger className="w-56 h-11" data-testid="audit-action-filter"><SelectValue placeholder="All actions" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All actions</SelectItem>
              {ACTIONS.filter(Boolean).map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}
            </SelectContent>
          </Select>
        } />
      <DataTable columns={columns} rows={data.items} loading={loading} error={error} onRetry={load} testid="audit-table"
        empty={{ title: "No audit entries", description: "System events will be recorded here.", icon: ScrollText }} />
      <Pagination page={page} pageSize={20} total={data.total} onPage={setPage} />
    </div>
  );
}
